import {
  DEFAULT_CUSTOM_COLUMNS,
  createCustomColumn,
  getAllColumnOptions,
} from './collegeColumns';

export function parseCsv(text) {
  const rows = [];
  let row = [];
  let value = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        value += character;
      }
    } else if (character === '"') {
      quoted = true;
    } else if (character === ',') {
      row.push(value);
      value = '';
    } else if (character === '\n') {
      row.push(value);
      rows.push(row);
      row = [];
      value = '';
    } else if (character !== '\r') {
      value += character;
    }
  }

  row.push(value);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows.filter((cells) => cells.some((cell) => cell.trim()));
}

function normalizeHeader(value) {
  return String(value || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const schoolNameAliases = new Set(['college', 'college name', 'university', 'university name', 'school']);

function isTruthy(value) {
  return ['true', 'yes', 'y', '1', 'complete', 'completed', 'checked'].includes(String(value).trim().toLowerCase());
}

export function prepareCsvDashboard(text) {
  const parsedRows = parseCsv(text);
  if (parsedRows.length < 2) {
    throw new Error('This CSV needs a header row and at least one row of information.');
  }

  const headers = parsedRows[0].map((header, index) => header.trim() || `Column ${index + 1}`);
  const knownColumns = getAllColumnOptions(DEFAULT_CUSTOM_COLUMNS);
  const knownByName = new Map();
  knownColumns.forEach((column) => {
    knownByName.set(normalizeHeader(column.label), column);
    knownByName.set(normalizeHeader(column.id), column);
  });
  schoolNameAliases.forEach((alias) => {
    knownByName.set(alias, knownColumns.find((column) => column.id === 'school_name'));
  });

  const newCustomColumns = [];
  const customByName = new Map();
  const mappings = headers.map((header, index) => {
    const normalized = normalizeHeader(header);
    const knownColumn = knownByName.get(normalized);
    if (knownColumn) return { index, header, column: knownColumn, custom: false };

    let customColumn = customByName.get(normalized);
    if (!customColumn) {
      customColumn = createCustomColumn(header, 'text');
      customByName.set(normalized, customColumn);
      newCustomColumns.push(customColumn);
    }
    return { index, header, column: customColumn, custom: true };
  });

  const activeColumns = Array.from(new Set(mappings.map((mapping) => mapping.column.id)));
  const rows = parsedRows.slice(1)
    .filter((cells) => cells.some((cell) => cell.trim()))
    .map((cells, rowIndex) => {
      const dashboardRow = {
        id: Date.now() + rowIndex,
        schoolData: {},
        category: '',
        date: '',
        checked: false,
        customFields: {},
        importedValues: {},
      };

      mappings.forEach(({ index, column }) => {
        const cellValue = String(cells[index] ?? '').trim();
        if (column.id === 'checkbox') dashboardRow.checked = isTruthy(cellValue);
        else if (column.id === 'category') dashboardRow.category = cellValue;
        else if (column.id === 'date') dashboardRow.date = cellValue;
        else if (column.type === 'custom') dashboardRow.customFields[column.id] = cellValue;
        else {
          dashboardRow.importedValues[column.id] = cellValue;
          if (column.id === 'school_name') {
            dashboardRow.schoolData['latest.school.name'] = cellValue;
          }
        }
      });
      return dashboardRow;
    });

  const matchedHeaders = mappings.filter((mapping) => !mapping.custom).map((mapping) => mapping.header);
  const customHeaders = mappings.filter((mapping) => mapping.custom).map((mapping) => mapping.header);

  return {
    activeColumns,
    customColumns: [...DEFAULT_CUSTOM_COLUMNS, ...newCustomColumns],
    rows,
    columnWidths: {},
    preview: {
      headers,
      rows: parsedRows.slice(1, 4),
      matchedHeaders,
      customHeaders,
      rowCount: rows.length,
    },
  };
}
